/* global views, showModalError, functionality, components, rulesFlow  */

import React, { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import Request from "common/Request";
import FilteringControls from "./FilteringControls";
import ActionsContext from "common/ActionsContext";
import { parseTotalFlows } from "./api";
import FlowsPerSubscriberHelpModal from "./Help";
import { getBqnIdCmdPart, safeStr } from "common/api";
import useProfile from "common/hooks/useProfile";
import { Header } from "common/Header";
import { atoiv6, atoiv4, durationToInt} from "common/utils";
import BqnContext, { useBqnId } from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import { NumberCell, TimeCell } from "common/graphs/Table/TableRenders";

const doesNothing = () => {};

const useFlowsData = ({ lines, subnet, policy }) => {
  const [request, setRequest] = useState(null);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const doLoad = useCallback( ()=>{
    setRequest(doRequest({lines, subnet, policy, bqnId, mode}));
  } , [lines, subnet, policy, bqnId, mode])
  return [request, doLoad];
}

const Report = ({
  setTotal = doesNothing,
  subnet,
  lines,
  policy,
  ..._otherSettings
}) => {
  const actions = useContext(ActionsContext);
  const bqnId = useBqnId();
  const [request, doLoad] = useFlowsData({lines, subnet, policy});

  const doLoadTotals = () =>
    ifCl.run("show flow usage").then(response => parseTotalFlows(response, bqnId)).then(setTotal);

  const update = () => {
    doLoad();
    doLoadTotals();
  };

  useEffect(() => {
    update();
    return actions.recv("do-load", () => {
      update();
    });
  }, [bqnId, lines, subnet, policy]);

  if (request === null) {
    return null;
  }

  return (
    <Request during={request}>{(result) => <ShowFlows {...result} />}</Request>
  );
};

const ShowTotal = ({ total = null }) =>
  total === null ? null : (
    <div className="row">
      <div className="margin-l-0 col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <span>Total Active flows&nbsp;&nbsp;</span>
        <span>{total}</span>
      </div>
    </div>
  );

const FlowsPerSubscriber = ({ returnView, policy }) => {
  const [total, setTotal] = useState(null);
  return (
    <div id="viewFlowsPerSubs">
      <div className="row clearfix">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className="card">
            <Header
              title="Flows Per Subscriber"
              backButton={!!returnView}
              contextMenu={<ContextMenu returnView={returnView} />}
            />
            <div className="body table-responsive">
              <ShowTotal total={total} />
              <FilteringControls policy={policy}>
                {(settings) => <Report {...settings} setTotal={setTotal} />}
              </FilteringControls>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowsPerSubscriber;

const doRequest = ({ subnet, lines, policy, bqnId, mode }) =>
  ifCl
    .run(
      `show  flow all ${subnet || "all"}${
        policy !== "all" ? " policy " + safeStr(policy) : ""
      } full lines ${lines}${getBqnIdCmdPart(mode, bqnId)}`
    )
    .then((response) => ({ response }));

const ContextMenu = ({ returnView }) => {
  const actions = useContext(ActionsContext);

  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
        <a
          onClick={() => actions.send("do-load")}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const openSubscriberDetails = ({ addr, subsId }) => {
  globalNavigate("viewSubsDetail", {
    addr,
    subsId,
    returnView: "viewFlowsPerSubs"
  });
};

const doEditPolicy = (policy) => {
  globalNavigate("viewPolicyFlowEdit", {returnView: "viewFlowsPerSubs", policy})
}

const getColumns = ({profile, mode}) => {
  const { hideRates = false, dpiEnabled = false } = profile;
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
      {
        label: "SUBS-ID",
        id: "SUBSCRIBER-ID",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick: (subsId) => openSubscriberDetails({ subsId }),
      },
      {
        label: "TYPE",
        id: "TYPE",
        numeric: false,
      },
      {
        label: "ACC-ADDR",
        id: "ACC-ADDR",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick:(addr) => openSubscriberDetails({ addr }),
        parse: (value) => {
          if(!value){
            return 0;
          }
          return value.includes(":") ? atoiv6(value) : atoiv4(value)
        },
        parseFilter: (value) => value
      },
      {
        label: "APORT",
        id: "APORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} thousands={false} hasNA={true} />
        ),
      },
      {
        label: "INET-ADDR",
        id: "INET-ADDR",
        numeric: false,
        parse: (value) => {
          if(!value){
            return 0;
          }
          return value.includes(":") ? atoiv6(value) : atoiv4(value)
        },
        parseFilter: (value) => value
      },
      {
        label: "IPORT",
        id: "IPORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} thousands={false} hasNA={true} />
        ),
      },
      {
        label: "PROTO",
        id: "PROTO",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={true} />
        ),
      },
      {
        label: "ST",
        id: "ST",
        numeric: false,
      },
      {
        label: "INS",
        id: "INS",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={false} precision={0}/>
        ),
      },
      {
        label: "FLOW-POLICY",
        id: "POLICY-FLOW",
        numeric: false,
        hidden: !dpiEnabled,
        onClick: hideRates ? null : doEditPolicy,
        cellClassName: hideRates ? null : "hyperlink-text",
      },
      {
        label: "MBYTES-DN",
        id: "MBYTES-DN",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} precision={3} />
        ),
      },
      {
        label: "MBYTES-UP",
        id: "MBYTES-UP",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} precision={3} />
        ),
      },
      {
        label: "LIFETIME",
        id: "LIFETIME",
        renderCell: (value) => <TimeCell value={value} />,
        numeric: true,
        parse: (data) => (data === "n/a" ? null : durationToInt(data)),
      },
      {
        label: "DPI-DOMAIN",
        id: "DOMAIN",
        hidden: !dpiEnabled,
        numeric: false,
      },
  ];
};

/*****************************************************
 * Flows Table
 * ****************************************************/
const ShowFlows = ({ response }) => {
  const profile = useProfile();
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext); 

  if(profile === null){
    return null;
  }

  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, {});
    return newEntry;
  });

  const columns =getColumns({profile, mode});

  return (
  <>
    <TableContainer>
       <Table
          isSelectable={false}
          isRowSelectable={false}
          data={newRows}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="flows-per-subscriber"
          rowsPerPageNr={50}
        />
    </TableContainer>
    <FlowsPerSubscriberHelpModal />
  </>
);
}

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;
